import React from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  useTheme,
  alpha,
} from '@mui/material';
import {
  School as SchoolIcon,
  TrendingUp as TrendingUpIcon,
  SupportAgent as SupportIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  Analytics as AnalyticsIcon,
  Verified as VerifiedIcon,
  CloudQueue as CloudIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import Logo from '../Logo.png';

const About = () => {
  const theme = useTheme();

  const values = [
    {
      icon: <SpeedIcon />,
      title: 'Performance First',
      description: 'Optimized for speed and efficiency, ensuring fast load times and smooth user experience.',
      color: theme.palette.primary.main,
    },
    {
      icon: <SecurityIcon />,
      title: 'Security Focused',
      description: 'Enterprise-grade security with email verification and secure authentication systems.',
      color: theme.palette.secondary.main,
    },
    {
      icon: <TrendingUpIcon />,
      title: 'Scalable Solutions',
      description: 'Built to grow with your business, handling increased traffic and content effortlessly.',
      color: theme.palette.success.main,
    },
    {
      icon: <SupportIcon />,
      title: '24/7 Support',
      description: 'Dedicated support team ready to assist you whenever you need help.',
      color: theme.palette.info.main,
    },
  ];

  const stats = [
    { number: '99.9%', label: 'Uptime Guarantee' },
    { number: '500+', label: 'Active Users' },
    { number: '10k+', label: 'Content Items' },
    { number: '24/7', label: 'Support Available' },
  ];

  const features = [
    {
      icon: <AnalyticsIcon />,
      title: 'Advanced Analytics',
      description: 'Track your content performance with detailed insights and metrics.',
    },
    {
      icon: <VerifiedIcon />,
      title: 'Role-Based Access',
      description: 'Secure role management for admins, super admins, and users.',
    },
    {
      icon: <CloudIcon />,
      title: 'Cloud Storage',
      description: 'Seamless integration with cloud storage for your media files.',
    },
    {
      icon: <SchoolIcon />,
      title: 'Easy Learning',
      description: 'Intuitive interface designed for quick adoption and ease of use.',
    },
  ];

  return (
    <Box sx={{ bgcolor: theme.palette.background.default, minHeight: '100vh' }}>
      {/* Hero Section */}
      <Box
        sx={{
          py: { xs: 8, sm: 10, md: 12 },
          bgcolor: theme.palette.background.default,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', maxWidth: 800, mx: 'auto' }}>
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Box
                component="img"
                src={Logo}
                alt="AediaX"
                sx={{
                  height: { xs: 50, sm: 60, md: 70 },
                  width: 'auto',
                  mb: 3,
                }}
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <Typography
                variant="h1"
                sx={{
                  fontWeight: 800,
                  mb: 2,
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                  letterSpacing: '-0.02em',
                  color: theme.palette.text.primary,
                }}
              >
                About AediaX
              </Typography>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                  color: theme.palette.text.secondary,
                  lineHeight: 1.6,
                }}
              >
                We're building the future of content management with powerful tools 
                and intuitive interfaces that help you manage your digital presence effortlessly.
              </Typography>
            </motion.div>
          </Box>
        </Container>
      </Box>

      {/* Mission Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, sm: 8, md: 10 } }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' },
                color: theme.palette.text.primary,
                mb: 2,
              }}
            >
              Our Mission
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.text.secondary,
                maxWidth: 700,
                mx: 'auto',
                fontSize: '1.1rem',
                lineHeight: 1.7,
              }}
            >
              To provide a seamless, secure, and powerful content management platform 
              that empowers businesses to take full control of their digital presence 
              with confidence and ease.
            </Typography>
          </Box>
        </motion.div>

        {/* Stats Section */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 4,
            justifyContent: 'center',
            my: 8,
          }}
        >
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              viewport={{ once: true }}
              style={{ flex: '1 1 200px', textAlign: 'center' }}
            >
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                  color: theme.palette.primary.main,
                  mb: 1,
                }}
              >
                {stat.number}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                }}
              >
                {stat.label}
              </Typography>
            </motion.div>
          ))}
        </Box>
      </Container>

      {/* Values Section */}
      <Box
        sx={{
          bgcolor: alpha(theme.palette.primary.main, 0.02),
          py: { xs: 8, sm: 10, md: 12 },
          borderTop: `1px solid ${theme.palette.divider}`,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' },
                  color: theme.palette.text.primary,
                  mb: 2,
                }}
              >
                Our Values
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: theme.palette.text.secondary,
                  maxWidth: 600,
                  mx: 'auto',
                }}
              >
                The principles that guide everything we do
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 3,
                justifyContent: 'center',
              }}
            >
              {values.map((value, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  style={{ flex: '1 1 280px', maxWidth: '350px' }}
                >
                  <Card
                    elevation={0}
                    sx={{
                      height: '100%',
                      textAlign: 'center',
                      p: 2,
                      borderRadius: 3,
                      bgcolor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.divider}`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: theme.shadows[4],
                        borderColor: alpha(value.color, 0.3),
                      },
                    }}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: '50%',
                          bgcolor: alpha(value.color, 0.08),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mx: 'auto',
                          mb: 2,
                        }}
                      >
                        <Box sx={{ fontSize: 30, color: value.color }}>
                          {value.icon}
                        </Box>
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          mb: 1.5,
                          color: theme.palette.text.primary,
                        }}
                      >
                        {value.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.text.secondary,
                          lineHeight: 1.6,
                        }}
                      >
                        {value.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, sm: 10, md: 12 } }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' },
                color: theme.palette.text.primary,
                mb: 2,
              }}
            >
              Key Features
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.text.secondary,
                maxWidth: 600,
                mx: 'auto',
              }}
            >
              Everything you need in a modern CMS
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 3,
              justifyContent: 'center',
            }}
          >
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                style={{ flex: '1 1 280px', maxWidth: '350px' }}
              >
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    p: 2,
                    borderRadius: 3,
                    bgcolor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateX(4px)',
                      borderColor: theme.palette.primary.main,
                    },
                  }}
                >
                  <CardContent>
                    <Box sx={{ color: theme.palette.primary.main, mb: 2 }}>
                      {feature.icon}
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        mb: 1,
                        color: theme.palette.text.primary,
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                        lineHeight: 1.6,
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </Box>
        </motion.div>
      </Container>

      
    </Box>
  );
};

export default About;